import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, IconButton, Box, styled } from '@mui/material';
import { Keyboard } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useClientNavigation } from '../../hooks/useClientNavigation.js';
import { PermissionsContext } from '../../Contexts/Permissions';
import Constants from '../../assests/Constants/constants';
import { getScreenFunctionKey, XTRAM_ORANGE } from '../../Utils/getScreenFunctionKey.js';

// Styled components using MUI
const KeyboardShortcutsButtonWrap = styled(Box)({
    position: 'relative',
    display: 'inline-flex',
    margin: 0,
});


const KeyboardShortcutsButton = styled(IconButton)(({ theme }) => ({
    background: '#ffffff !important',
    color: '#0e2b58 !important',
    border: '1px solid #e8ebf2 !important',
    borderRadius: '14px !important',
    width: 'var(--xtram-add-btn-height, 44px) !important',
    height: 'var(--xtram-add-btn-height, 44px) !important',
    boxShadow: '0 1px 3px rgba(20, 30, 43, 0.06) !important',
    zIndex: 1,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease !important',
    '& .MuiSvgIcon-root': {
        fontSize: 'var(--xtram-header-icon, 20px) !important',
    },
    '&:hover': {
        background: '#ffffff !important',
        transform: 'translateY(-1px)',
        borderColor: '#d7ddf5 !important',
        boxShadow: '0 6px 16px rgba(49, 79, 255, 0.14) !important',
    },
    [theme.breakpoints.down('md')]: {
        width: 'var(--xtram-control-height, 40px) !important',
        height: 'var(--xtram-control-height, 40px) !important',
        borderRadius: '12px !important',
    },
}));

const TooltipContent = styled(Box)(({ theme, direction }) => ({
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)',
    color: 'white',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: 'var(--xtram-panel-head, 11px)',
    minWidth: 'calc(var(--xtram-menu-width, 220px) + 20px)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    direction: direction,
    '& .shortcuts-title': {
        fontWeight: '700',
        marginBottom: '20px',
        fontSize: 'var(--xtram-panel-kpi-value, 16px)',
        borderBottom: '2px solid rgba(255, 255, 255, 0.15)',
        paddingBottom: '12px',
        textAlign: 'center',
        letterSpacing: '0.5px',
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    },
    '& .shortcut-item': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        cursor: 'pointer',
        borderRadius: '6px',
        marginBottom: '4px',
        transition: 'all 0.3s ease',
        border: '1px solid transparent',
        flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
        width: '100%',
        background: 'transparent',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'inherit',
        boxSizing: 'border-box !important',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transform: direction === 'rtl' ? 'translateX(-2px)' : 'translateX(2px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
        '&:last-child': {
            marginBottom: '0',
        },
        '&.clickable': {
            cursor: 'pointer',
        },
    },
    '& .shortcut-key': {
        backgroundColor: 'rgba(48, 79, 255, 0.8)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: 'var(--xtram-panel-badge, 10px)',
        fontWeight: '700',
        fontFamily: 'monospace',
        letterSpacing: '0.5px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minWidth: 'var(--xtram-summary-chip-height, 28px)',
        textAlign: 'center',
        display: 'inline-block',
        order: direction === 'rtl' ? 2 : 1,
    },
    '& .shortcut-key-current': {
        backgroundColor: XTRAM_ORANGE,
        color: '#0e1621',
        border: '1px solid rgba(0, 0, 0, 0.08)',
    },
    '& .shortcut-action': {
        marginLeft: direction === 'rtl' ? '0' : '12px',
        marginRight: direction === 'rtl' ? '12px' : '0',
        flex: 1,
        fontSize: 'var(--xtram-text-xs, 12px)',
        fontWeight: '500',
        color: '#f0f0f0',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        order: direction === 'rtl' ? 1 : 2,
        textAlign: direction === 'rtl' ? 'right' : 'left',
        padding: '4px 0',
    },
}));

const InlineShortcutsContainer = styled(Box)(({ direction }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'visible',
    justifyContent: 'flex-start',
    marginInlineStart: direction === 'rtl' ? '0' : '10px',
    marginInlineEnd: direction === 'rtl' ? '10px' : '0',
    maxWidth: '100%',
    padding: '3px 0 0 0',
    borderRadius: '6px',
    background: 'var(--cac-icon-bg, #F0F5FF)',
    color: '#ebf6ff',
    border: '1px solid color-mix(in srgb, var(--cac-accent, #1C63E8) 22%, #ffffff)',
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    '&::-webkit-scrollbar': {
        height: '2px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
}));

const InlineShortcutButtonWrapper = styled('div')({
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
    overflow: 'visible',
    borderInlineEnd: '1px solid color-mix(in srgb, var(--cac-accent, #1C63E8) 18%, #ffffff)',
    '&:last-of-type': {
        borderInlineEnd: 'none',
    },
});

const InlineShortcutButton = styled('button')(({ direction }) => ({
    position: 'relative',
    border: 'none',
    background: 'var(--cac-icon-bg, #F0F5FF)',
    color: 'var(--theme-tag-color, #181818)',
    borderRadius: 0,
    padding: '2px 4px',
    fontSize: 'var(--xtram-header-compact-font, 12px)',
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    textAlign: direction === 'rtl' ? 'left' : 'right',
    boxShadow: 'none',
    minHeight: 'var(--xtram-unprocessed-height, 26px)',
    width: 'max-content',
    maxWidth: 'none',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    '&.has-fn-key-tag': {
        /* F-key tag: physical top-left */
        ...(direction === 'ltr'
            ? { paddingInlineEnd: 'var(--xtram-header-icon, 20px)' }
            : { paddingInlineStart: 'var(--xtram-header-icon, 20px)' }),
    },
    '&.has-count-badge': {
        /* Count tag: physical top-right */
        ...(direction === 'ltr'
            ? { paddingInlineStart: 'var(--xtram-header-icon, 20px)' }
            : { paddingInlineEnd: 'var(--xtram-header-icon, 20px)' }),
    },
    '&.has-count-badge.has-fn-key-tag': {
        paddingInlineStart: 'var(--xtram-header-icon, 20px)',
        paddingInlineEnd: 'var(--xtram-header-icon, 20px)',
    },
    '&:hover': {
        background: 'color-mix(in srgb, var(--cac-accent, #1C63E8) 12%, var(--cac-icon-bg, #F0F5FF))',
        color: '#ffffff',
    },
    '&:active': {
        transform: 'none',
        background: 'var(--cac-accent, #1C63E8)',
        color: '#ffffff',
    },
    '&:focus-visible': {
        outline: 'none',
        background: 'var(--cac-accent, #1C63E8)',
        color: '#ffffff',
    },
    '&.active-shortcut': {
        background: 'var(--cac-accent, #1C63E8)',
        color: '#ffffff',
        fontSize: 'var(--xtram-text-xs, 0.875rem)',
        fontWeight: 700,
    },
    '&.active-shortcut:hover': {
        background: 'var(--cac-accent, #1C63E8)',
        color: '#ffffff',
    },
    '&.reporting-follow-up': {
        background: XTRAM_ORANGE,
        color: '#0e1621',
    },
    '&.reporting-follow-up:hover': {
        background: '#e09718',
        color: '#0e1621',
    },
}));

/* Inside the button top corner: stays inside tab (no overflow clip from scroll row) */
const inlineCornerBadgeBase = {
    position: 'absolute',
    top: -3,
    minWidth: 'calc(var(--xtram-panel-badge, 10px) * 1.5)',
    height: 'calc(var(--xtram-unprocessed-height, 26px) / 2)',
    padding: '1px 4px',
    fontSize: 'var(--xtram-panel-badge, 10px)',
    lineHeight: 'var(--xtram-header-compact-font, 12px)',
    fontWeight: 600,
    textAlign: 'center',
    pointerEvents: 'none',
    zIndex: 3,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    boxSizing: 'border-box',
};

const InlineShortcutCountBadge = styled('span')(() => ({
    ...inlineCornerBadgeBase,
    right: -3,
    left: 'auto',
    borderRadius: '0 0 0 10px',
    background: '#304FFF',
    color: '#ffffff',
}));

/** Same corner-tag shape as count badge, orange, on the physical top-left of the tab */
const InlineShortcutFnKeyBadge = styled('span')(() => ({
    ...inlineCornerBadgeBase,
    left: -3,
    right: 'auto',
    borderRadius: '0 0 10px 0',
    background: XTRAM_ORANGE,
    color: '#0e1621',
}));

const isFunctionKey = (key) => typeof key === 'string' && /^F\d{1,2}$/.test(key);

const INLINE_ADVANCE_CALC_KEY = '__inlineAdvanceCalculator';
const INLINE_GRANTS_STATUS_KEY = '__inlineGrantsStatus';
const INLINE_CLIENT_PAGE_KEY = '__inlineClientPage';
const INLINE_REPORTING_FOLLOW_UP_KEY = '__inlineReportingFollowUp';

const KeyboardShortcuts = ({
    client,
    direction,
    displayMode = 'icon',
    enableKeyboardListener = true,
    inlineExcludeKeys = [],
    hideStatusBadge = false,
    /** When true (with displayMode inlineLabels), appends Advance Calculator as a clickable tab only — not in F-key list or keyboard tooltip. */
    advanceCalculatorInline = false,
    /** With displayMode inlineLabels, appends a Monthly Reporting Tracking tab that calls this. */
    onOpenReportingFollowUp = null,
}) => {
    
    const { t } = useTranslation();
    const location = useLocation();
    const [showTooltip, setShowTooltip] = useState(false);
    const inlineScrollRef = useRef(null);
    const currentScreenFnKey = getScreenFunctionKey(location.pathname, location.state?.type);
    const { userTypeContext } = useContext(PermissionsContext);
    
    const {
        viewDocumentDetails,
        viewPaymentAcceptanceDetails,
        viewJournalEntryDetails,
        viewProfitAndLossDetails,
        viewLedgerReport,   
        viewAccountCardsTable,
        viewAccountCardDashboard,
        viewAccountantDetails,
        navigateToPrevious,
        viewAdvanceCalculator,
        viewGrantsStatus,
        viewClientDetails,
    } = useClientNavigation(client);

    const shortcuts = useMemo(() => ([
        {
            key: 'F1',
            action: t('Incomes'),
            count: hideStatusBadge ? undefined : (client?.documentCounts?.incomes ?? 0),
            onClick: () => {
                viewDocumentDetails('Incomes');
                setShowTooltip(false);
            }
        },
        {
            key: 'F2',
            action: t('Expenses'),
            count: hideStatusBadge ? undefined : (client?.documentCounts?.expenses ?? 0),
            onClick: () => {
                viewDocumentDetails('Expenses');
                setShowTooltip(false);
            }
        },
        {
            key: 'F3',
            action: t('PaymentAcceptance'),
            count: hideStatusBadge ? undefined : (client?.documentCounts?.checks ?? 0),
            onClick: () => {
                viewPaymentAcceptanceDetails('PaymentAcceptances');
                setShowTooltip(false);
            }
        },
        {
            key: 'F4',
            action: t('JournalEntry'),
            count: hideStatusBadge ? undefined : (client?.documentCounts?.journalEntries ?? 0),
            onClick: () => {
                viewJournalEntryDetails('JournalEntries');
                setShowTooltip(false);
            }
        },
        {
            key: 'F5',
            action: t('SortCodeReport'),
            onClick: () => viewProfitAndLossDetails("ProfitAndLossReport"),
            subItems: null
        },
        {
            key: 'F6',
            action: t('TrialBalance'),
            onClick: () => viewLedgerReport("TrialBalance"),
            subItems: null
        },
        {
            key: 'F7',
            action: t('ReportDetailedProfitAndLoss'),
            onClick: () => viewLedgerReport("ReportDetailedProfitAndLoss", true),
            subItems: null
        },
        {
            key: 'F8',
            action: t('AccountCardsTable'),
            onClick: () => {
                viewAccountCardsTable('AccountCardsTable');
                setShowTooltip(false);
            }
        },
        {
            key: 'F9',
            action: t('LedgerReport'),
            onClick: () => {
                viewAccountCardDashboard();
                setShowTooltip(false);
            }
        },
        ...(userTypeContext?.toString() !== Constants.UserType.Client ? [{
            key: 'F10',
            action: t('AccountantDetails'),
            onClick: () => {
                viewAccountantDetails();
                setShowTooltip(false);
            }
        }] : []),
        {
            key: 'F11',
            action: t('GoBackIntoPreviousScreen'),
            onClick: () => {
                navigateToPrevious(location);
                setShowTooltip(false);
            }
        },
        {
            key: 'F12',
            action: t('PasteFromPreviousRow') || 'Paste from Previous Row (Same Field)',
            onClick: () => {
                setShowTooltip(false);
            }
        },
    ]), [
        t,
        client,
        hideStatusBadge,
        userTypeContext,
        viewDocumentDetails,
        viewPaymentAcceptanceDetails,
        viewJournalEntryDetails,
        viewProfitAndLossDetails,
        viewLedgerReport,
        viewAccountCardsTable,
        viewAccountCardDashboard,
        viewAccountantDetails,
        navigateToPrevious,
        location,
    ]);

    useEffect(() => {
        if (!enableKeyboardListener) {
            return undefined;
        }

        const normalizeFunctionKey = (e) => {
            if (typeof e?.key === 'string' && /^F\d{1,2}$/.test(e.key)) return e.key;
            if (typeof e?.code === 'string' && /^F\d{1,2}$/.test(e.code)) return e.code;
            return null;
        };

        const handleKeyDown = (e) => {
            const fnKey = normalizeFunctionKey(e);
            if (!fnKey) return;

            // Document / PaymentAcceptance edit modals own F1–F4 while open.
            if (
                ['F1', 'F2', 'F3', 'F4'].includes(fnKey) &&
                document.body.getAttribute('data-document-edit-modal-open') === 'true'
            ) {
                return;
            }

            // Bank Matching and Card Matching own F5 (match) and F6 (per-row
            // journal entries); let the event fall through to their handlers.
            const matchingScreenPath = location.pathname.toLowerCase();
            if (
                (fnKey === 'F5' || fnKey === 'F6') &&
                (matchingScreenPath.startsWith('/banktransactions') ||
                    matchingScreenPath.startsWith('/cardmatching'))
            ) {
                return;
            }

            const shortcut = shortcuts.find((s) => s.key === fnKey);
            if (!shortcut?.onClick) return;

            // Block browser defaults (e.g. F11 fullscreen) before other global handlers run
            e.preventDefault();
            e.stopImmediatePropagation();

            if (fnKey === 'F11') {
                navigateToPrevious(location);
                setShowTooltip(false);
                return;
            }

            shortcut.onClick();
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [shortcuts, enableKeyboardListener, navigateToPrevious, location]);

    // Keep horizontal scroll at the logical start (RTL = ClientPage on the right) on small screens
    useLayoutEffect(() => {
        if (displayMode !== 'inlineLabels') return;
        const el = inlineScrollRef.current;
        if (!el) return;

        const scrollToDirStart = () => {
            el.scrollLeft = 0;
        };

        scrollToDirStart();
        const raf = requestAnimationFrame(scrollToDirStart);
        return () => cancelAnimationFrame(raf);
    }, [displayMode, direction, location.pathname, client?.caseId, client?.id]);

    if (displayMode === 'inlineLabels') {
        const filtered = shortcuts.filter(
            (shortcut) => !inlineExcludeKeys.includes(shortcut.key)
        );
        const advanceShortcut =
            advanceCalculatorInline && client
                ? [
                      {
                          key: INLINE_ADVANCE_CALC_KEY,
                          action: t('AdvanceDynamicCalculator'),
                          onClick: () => {
                              viewAdvanceCalculator('AdvanceDynamicCalculator');
                          },
                      },
                  ]
                : [];

        const grantsShortcut = client
            ? [
                  {
                      key: INLINE_GRANTS_STATUS_KEY,
                      action: t('grantsStatus.inlineButton'),
                      onClick: () => viewGrantsStatus('GrantsStatus'),
                  },
              ]
            : [];

        const clientPageShortcut = client
            ? [
                  {
                      key: INLINE_CLIENT_PAGE_KEY,
                      action: t('ClientPage'),
                      onClick: () => viewClientDetails(),
                  },
              ]
            : [];

        const reportingFollowUpShortcut = client && onOpenReportingFollowUp
            ? [
                  {
                      key: INLINE_REPORTING_FOLLOW_UP_KEY,
                      action: t('ReportingFollowUp'),
                      onClick: onOpenReportingFollowUp,
                  },
              ]
            : [];

        const inlineShortcuts = [
            ...clientPageShortcut,
            ...filtered,
            ...advanceShortcut,
            ...grantsShortcut,
            ...reportingFollowUpShortcut,
        ];
        const currentPath = location.pathname.toLowerCase();
        const currentType = location.state?.type;

        const isShortcutActive = (shortcutKey) => {
            switch (shortcutKey) {
                case INLINE_CLIENT_PAGE_KEY:
                    return currentPath.startsWith('/client-details');
                case 'F1':
                    return currentPath.startsWith('/document') && currentType === 'Incomes';
                case 'F2':
                    return currentPath.startsWith('/document') && currentType === 'Expenses';
                case 'F3':
                    return currentPath.startsWith('/paymentacceptance');
                case 'F4':
                    return currentPath.startsWith('/journalentry');
                case 'F5':
                    return currentPath.startsWith('/profitandlossreport');
                case 'F6':
                    return currentPath.startsWith('/ledgerreport') && currentType === 'TrialBalance';
                case 'F7':
                    return currentPath.startsWith('/ledgerreport') && currentType === 'ReportDetailedProfitAndLoss';
                case 'F8':
                    return currentPath.startsWith('/accountcardstable');
                case 'F9':
                    return currentPath.startsWith('/accountcarddashboard');
                case 'F10':
                    return currentPath.startsWith('/accountant-details');
                case INLINE_ADVANCE_CALC_KEY:
                    return (
                        currentPath.startsWith('/advancecalculator') &&
                        currentType === 'AdvanceDynamicCalculator'
                    );
                case INLINE_GRANTS_STATUS_KEY:
                    return currentPath.startsWith('/grantsstatus');
                default:
                    return false;
            }
        };

        return (
            <>
                <InlineShortcutsContainer
                    ref={inlineScrollRef}
                    direction={direction}
                    dir={direction}
                >
                    {inlineShortcuts.map((shortcut, index) => (
                        <Tooltip
                            key={`${shortcut.key}-${index}`}
                            title={
                                isFunctionKey(shortcut.key)
                                    ? `${shortcut.key} — ${shortcut.action}`
                                    : shortcut.action
                            }
                            arrow
                            placement="bottom"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        fontFamily: 'Montserrat',
                                        fontSize: 'var(--xtram-header-compact-font, 12px)',
                                        fontWeight: 600,
                                        bgcolor: '#304FFF',
                                    },
                                },
                                arrow: { sx: { color: '#304FFF' } },
                            }}
                        >
                            <InlineShortcutButtonWrapper>
                                <InlineShortcutButton
                                    type="button"
                                    direction={direction}
                                    onClick={shortcut.onClick}
                                    className={[
                                        isShortcutActive(shortcut.key) ? 'active-shortcut' : '',
                                        shortcut.key === INLINE_REPORTING_FOLLOW_UP_KEY ? 'reporting-follow-up' : '',
                                        isFunctionKey(shortcut.key) ? 'has-fn-key-tag' : '',
                                        shortcut.count !== null && shortcut.count !== undefined && shortcut.count > 0 ? 'has-count-badge' : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    {shortcut.count !== null && shortcut.count !== undefined && shortcut.count > 0 && (
                                        <InlineShortcutCountBadge>{shortcut.count}</InlineShortcutCountBadge>
                                    )}
                                    {isFunctionKey(shortcut.key) && (
                                        <InlineShortcutFnKeyBadge>{shortcut.key}</InlineShortcutFnKeyBadge>
                                    )}
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            whiteSpace: 'nowrap',
                                            fontSize: 'inherit',
                                            fontWeight: 'inherit',
                                            color: 'inherit',
                                        }}
                                    >
                                        {shortcut.action}
                                    </Box>
                                </InlineShortcutButton>
                            </InlineShortcutButtonWrapper>
                        </Tooltip>
                    ))}
                </InlineShortcutsContainer>
            </>
        );
    }

    const tooltipContent = (
        <TooltipContent dir={direction}>
            <div className="shortcuts-title">{t('KeyboardShortcuts')}</div>
            {shortcuts.map((shortcut, index) => (
                <button
                    type="button"
                    dir={direction}
                    key={index}
                    className="shortcut-item clickable"
                    onClick={shortcut.onClick}
                >
                    <span
                        className={`shortcut-key${shortcut.key === currentScreenFnKey ? ' shortcut-key-current' : ''}`}
                    >
                        {shortcut.key}
                    </span>
                    <span className="shortcut-action">{shortcut.action}</span>
                </button>
            ))}
        </TooltipContent>
    );

        return (
        <Tooltip
            direction={direction}
            title={tooltipContent}
            open={showTooltip}
            onClose={() => setShowTooltip(false)}
            onOpen={() => setShowTooltip(true)}
            placement="right"
            arrow
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [8, 0],
                        },
                    },
                ],
            }}
        >
            <KeyboardShortcutsButtonWrap>
                {currentScreenFnKey && (
                    <InlineShortcutFnKeyBadge
                        sx={{
                            top: -4,
                            right: -5,
                            left: 'auto',
                            borderRadius: '0 0 0 10px',
                        }}
                    >
                        {currentScreenFnKey}
                    </InlineShortcutFnKeyBadge>
                )}
                <KeyboardShortcutsButton
                    onClick={() => setShowTooltip(!showTooltip)}
                    size="small"
                    title={
                        currentScreenFnKey
                            ? `${t('KeyboardShortcuts')} — ${currentScreenFnKey}`
                            : t('KeyboardShortcuts')
                    }
                >
                    <Keyboard />
                </KeyboardShortcutsButton>
            </KeyboardShortcutsButtonWrap>
        </Tooltip>
    );
};

export default KeyboardShortcuts; 